import { InstrumentModelTypeEnum } from "../../types.generated";

// Milliseconds per day 24*60*60*1000 = 86400000
const MS_PER_DAY = 86400000;
export const yearFracActual365 = (d1: Date, d2: Date) => (d2.getTime() - d1.getTime()) / 365.0 / MS_PER_DAY;

// Reference tenor for the CDS index hedge divisor, e.g. 5Y.
export const CDS_REFERENCE_TENOR = 5;

export const calculateDivisor = (maturityDate: string, referenceTenor: number) => {
    const roundedMaturityYears = Math.round(yearFracActual365(new Date(), new Date(maturityDate)));
    return referenceTenor / Math.max(roundedMaturityYears, 1);
};

export type CdsIndexFamily = "CDX" | "ITRAXX";

export type CdsIndexSegment = "IG" | "HY";

export interface CdsIndexName {
    family: CdsIndexFamily;
    segment: CdsIndexSegment;
    series: number | null;
    tenorYears: number | null;
}

const CDS_INDEX_FAMILY_TOKENS: Record<string, CdsIndexFamily> = {
    CDX: "CDX",
    ITRX: "ITRAXX",
    ITRAXX: "ITRAXX"
};

// Sub-index tokens across naming conventions, e.g. "CDX HY", "CDX-NAHY", "CDX.NA.HY", "ITRX XOVER",
// "ITRAXX-Xover", "iTraxx Crossover" for high yield and "CDX IG", "CDX-NAIG", "ITRX EUR", "ITRAXX-Europe",
// "iTraxx Main" for investment grade.
const CDS_INDEX_SEGMENT_TOKENS: Record<string, CdsIndexSegment> = {
    HY: "HY",
    NAHY: "HY",
    XOVER: "HY",
    CROSSOVER: "HY",
    IG: "IG",
    NAIG: "IG",
    EUR: "IG",
    EUROPE: "IG",
    MAIN: "IG"
};

const CDS_INDEX_SERIES_PATTERN = /^S(\d+)$/;
const CDS_INDEX_TENOR_PATTERN = /^(\d+)Y$/;

/**
 * Reads family, segment, series and tenor out of a CDS index name regardless of naming convention,
 * e.g. the old Bloomberg style "CDX HY CDSI S46 5Y PRC" and "ITRX XOVER CDSI S45 5Y" as well as the
 * new "CDX-NAHY-S47-5Y" and "ITRAXX-Xover-S46-5Y". Returns null when the family or segment is not recognized.
 */
export const parseCdsIndexName = (name: string | null | undefined): CdsIndexName | null => {
    if (!name) {
        return null;
    }

    const tokens = name
        .toUpperCase()
        .split(/[\s_.\-/]+/)
        .filter((token) => token !== "");

    let family: CdsIndexFamily | null = null;
    let segment: CdsIndexSegment | null = null;
    let series: number | null = null;
    let tenorYears: number | null = null;

    tokens.forEach((token) => {
        if (family === null && CDS_INDEX_FAMILY_TOKENS[token]) {
            family = CDS_INDEX_FAMILY_TOKENS[token];
            return;
        }
        if (segment === null && CDS_INDEX_SEGMENT_TOKENS[token]) {
            segment = CDS_INDEX_SEGMENT_TOKENS[token];
            return;
        }
        const seriesMatch = CDS_INDEX_SERIES_PATTERN.exec(token);
        if (series === null && seriesMatch) {
            series = Number(seriesMatch[1]);
            return;
        }
        const tenorMatch = CDS_INDEX_TENOR_PATTERN.exec(token);
        if (tenorYears === null && tenorMatch) {
            tenorYears = Number(tenorMatch[1]);
        }
    });

    if (family === null || segment === null) {
        return null;
    }

    return { family, segment, series, tenorYears };
};

export const isHighYieldCdsIndex = (name: string | null | undefined): boolean => {
    return parseCdsIndexName(name)?.segment === "HY";
};

// Minimal shape of a trading manager position needed for CDS exposure math.
export interface CdsPosition {
    modelType?: InstrumentModelTypeEnum;
    quantity?: number;
    fxRate?: number;
    maturityDate?: string;
    instrument?: {
        model?: {
            legs?: { notional?: number }[];
        };
    };
}

// Signed notional in portfolio currency contributed by a single position.
// CDS index positions add quantity * fxRate; CDS basket (hedge) positions subtract
// (legNotional / divisor) * quantity * fxRate. Non-CDS positions contribute 0.
export const cdsNotionalPortfolioCcy = (item: CdsPosition, referenceTenor: number = CDS_REFERENCE_TENOR): number => {
    if (item.modelType === InstrumentModelTypeEnum.CdsIndex) {
        return item.quantity * item.fxRate;
    }
    if (item.modelType === InstrumentModelTypeEnum.CdsBasket) {
        const divisor = calculateDivisor(item.maturityDate, referenceTenor);
        const legNotional = item.instrument?.model?.legs?.[0]?.notional ?? 0;
        return -(legNotional / divisor) * item.quantity * item.fxRate;
    }
    return 0;
};

// CDS exposure ratio to NAV, i.e. the SUM row "Ratio to nav" of the "CDS Exposure"
// table on the Pre trade page. Returns undefined when there are no CDS positions or NAV is zero.
export const getCdsExposureRatio = (
    positions: CdsPosition[],
    nav: number,
    referenceTenor: number = CDS_REFERENCE_TENOR
): number | undefined => {
    const cdsPositions = positions.filter(
        (p) => p.modelType === InstrumentModelTypeEnum.CdsIndex || p.modelType === InstrumentModelTypeEnum.CdsBasket
    );
    if (cdsPositions.length === 0 || !nav) {
        return undefined;
    }
    const sumNotional = cdsPositions.reduce((acc, item) => acc + cdsNotionalPortfolioCcy(item, referenceTenor), 0);
    return sumNotional / nav;
};
